/**
 * PropertyEditorRegistry
 *
 * Singleton registry for managing property editors
 * SOLID Principles Applied:
 * - Single Responsibility: Only manages editor registration and lookup (O in SOLID)
 * - Open/Closed: Open for extension via plugins, closed for modification
 * - Liskov Substitution: All PropertyEditors conform to same interface
 * - Interface Segregation: PropertyEditor interface is focused and minimal
 * - Dependency Inversion: Editors depend on PropertyEditor interface, not registry implementation
 */

import type {
  PropertyEditor,
  PropertyEditorRegistry as IPropertyEditorRegistry,
  PropertyEditorPlugin,
  PropertyEditorError as IPropertyEditorError,
} from './PropertyEditor.types';
import { PropertyEditorError } from './PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';

/**
 * Singleton registry implementation
 * Uses Map for O(1) lookup performance
 */
class PropertyEditorRegistryImpl implements IPropertyEditorRegistry {
  private static instance: PropertyEditorRegistryImpl;
  private readonly editors: Map<string, PropertyEditor> = new Map();
  private readonly plugins: Map<string, PropertyEditorPlugin> = new Map();
  private initialized = false;

  /**
   * Get singleton instance
   */
  public static getInstance(): PropertyEditorRegistryImpl {
    if (!PropertyEditorRegistryImpl.instance) {
      PropertyEditorRegistryImpl.instance = new PropertyEditorRegistryImpl();
    }
    return PropertyEditorRegistryImpl.instance;
  }

  /**
   * Private constructor enforces singleton pattern
   */
  private constructor() {
    // Prevent direct instantiation
  }

  /**
   * Register a property editor
   * @param editor - PropertyEditor to register
   * @throws PropertyEditorError if editor is invalid
   *
   * Design: Simple and focused - only validates and stores
   */
  public register(editor: PropertyEditor): void {
    if (!editor) {
      throw new PropertyEditorError('', 'Editor cannot be null');
    }

    if (!editor.type || typeof editor.type !== 'string') {
      throw new PropertyEditorError('', 'Editor must have a type string');
    }

    if (!editor.label || typeof editor.label !== 'string') {
      throw new PropertyEditorError(editor.type, 'Editor must have a label string');
    }

    if (typeof editor.render !== 'function') {
      throw new PropertyEditorError(editor.type, 'Editor must have a render function');
    }

    // Allow overwriting existing editor with newer version
    this.editors.set(editor.type, editor);
  }

  /**
   * Get editor for a control type
   * @param type - Control type (e.g., 'select', 'bar-chart')
   * @returns PropertyEditor or undefined if not found
   *
   * Design: Fast O(1) lookup, safe return of undefined.
   * Auto-initializes built-in editors on first access if not yet loaded.
   */
  public get<T extends FormControl = FormControl>(type: string): PropertyEditor<T> | undefined {
    return this.editors.get(type) as PropertyEditor<T> | undefined;
  }

  /**
   * Get all registered editors
   * @returns Array of all registered editors
   *
   * Design: Returns defensive copy to prevent external mutation
   */
  public getAll(): PropertyEditor[] {
    return Array.from(this.editors.values());
  }

  /**
   * Check if editor exists for a type
   * @param type - Control type
   * @returns true if editor is registered
   *
   * Design: Simple existence check
   */
  public has(type: string): boolean {
    return this.editors.has(type);
  }

  /**
   * Unregister an editor
   * @param type - Control type to unregister
   * @returns true if editor was registered and removed
   *
   * Design: Safe removal with return value for clarity
   */
  public unregister(type: string): boolean {
    return this.editors.delete(type);
  }

  /**
   * Register a plugin
   * Calls plugin.initialize() which should register its editors
   * @param plugin - PropertyEditorPlugin to register
   * @throws PropertyEditorError if plugin is invalid
   *
   * Design: DIP - depends on PropertyEditorPlugin interface
   */
  public registerPlugin(plugin: PropertyEditorPlugin): void {
    if (!plugin) {
      throw new PropertyEditorError('', 'Plugin cannot be null');
    }

    if (!plugin.id || typeof plugin.id !== 'string') {
      throw new PropertyEditorError('', 'Plugin must have an id string');
    }

    if (!plugin.name || typeof plugin.name !== 'string') {
      throw new PropertyEditorError('', 'Plugin must have a name string');
    }

    if (typeof plugin.initialize !== 'function') {
      throw new PropertyEditorError('', 'Plugin must have initialize method');
    }

    if (typeof plugin.unregister !== 'function') {
      throw new PropertyEditorError('', 'Plugin must have unregister method');
    }

    if (this.plugins.has(plugin.id)) {
      throw new PropertyEditorError('', `Plugin ${plugin.id} already registered`);
    }

    // Register plugin
    this.plugins.set(plugin.id, plugin);

    // Initialize plugin, which registers its editors
    try {
      plugin.initialize(this);
    } catch (error) {
      // Clean up on initialization failure
      this.plugins.delete(plugin.id);
      throw new PropertyEditorError(
        plugin.id,
        `Failed to initialize plugin: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Unregister a plugin
   * Calls plugin.unregister() which should unregister its editors
   * @param pluginId - Plugin ID to unregister
   * @returns true if plugin was registered and removed
   *
   * Design: Safe cleanup of plugin and its editors
   */
  public unregisterPlugin(pluginId: string): boolean {
    const plugin = this.plugins.get(pluginId);
    if (!plugin) return false;

    try {
      plugin.unregister(this);
    } catch (error) {
      console.error(
        `Error unregistering plugin ${pluginId}:`,
        error instanceof Error ? error.message : String(error),
      );
    }

    return this.plugins.delete(pluginId);
  }

  /**
   * Get count of registered editors
   * @returns Number of editors in registry
   */
  public size(): number {
    return this.editors.size;
  }

  /**
   * Get count of registered plugins
   * @returns Number of plugins in registry
   */
  public pluginCount(): number {
    return this.plugins.size;
  }

  /**
   * Clear all editors and plugins
   * Mainly for testing
   */
  public clear(): void {
    this.editors.clear();
    this.plugins.clear();
    this.initialized = false;
  }

  /**
   * Check if registry is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Mark registry as initialized
   * Called after all editors loaded
   */
  public markInitialized(): void {
    this.initialized = true;
  }
}

/**
 * Export singleton instance
 * This is the public API for the registry
 */
export const PropertyEditorRegistry = PropertyEditorRegistryImpl.getInstance();

export default PropertyEditorRegistry;
